import axios, { AxiosInstance, AxiosRequestHeaders } from "axios";
import {
  AsicSearchParams,
  CreateAirportRequest,
  CreateAuditPolicyRequest,
  CreateLocationAuditConfigurationRequest,
  CreateLocationRequest,
  CreateReportConfigurationRequest,
  CreateSecurityCheckRequest,
  CreateSecurityTierRequest,
  CreateUserRequest,
  DashboardDataset,
  DashboardForecast,
  DashboardForecastSummary,
  DashboardStats,
  PersonSecuritySubmissionSearchParams,
  SelfServeAuditSearchParams,
  UpdateAirportRequest,
  UpdateAuditPolicyRequest,
  SaveLocationAccessLevelRequest,
  SaveLocationOperationalNeedRequest,
  UpdateLocationRequest,
  UpdateReportConfigurationRequest,
  UpdateSecurityCheckRequest,
  UpdateSecurityTierRequest,
  UpdateUserRequest,
  UpdateOfflineAccessConfigRequest,
} from "../types";

class ApiV3 {
  token: string | null;
  tokenExpiry: Date | null;
  client: AxiosInstance;
  apiBaseUrl: string;

  constructor() {
    this.apiBaseUrl = process.env.REACT_APP_API_V3_BASE_URL ?? "";
    this.token = localStorage.getItem("v3Token");
    this.tokenExpiry = localStorage.getItem("v3TokenExpiry")
      ? new Date(localStorage.getItem("v3TokenExpiry")!)
      : null;

    const headers: AxiosRequestHeaders = {
      Accept: "application/json",
      "Content-Type": "application/json",
    };

    if (this.token) {
      headers.Authorization = `Bearer ${this.token}`;
    }

    this.client = axios.create({
      baseURL: this.apiBaseUrl,
      timeout: 31000,
      headers: headers,
    });

    this.client.interceptors.request.use((request) => {
      if (!this.tokenExpiry || (this.tokenExpiry && new Date() > this.tokenExpiry)) {
        if (!window.location.href.includes("/signin")) {
          window.location.href = "/signout";
        }
      }
      return request;
    });

    this.client.interceptors.response.use(
      (response) => response,
      (error) => {
        if ([401, 403].includes(error.response?.status)) {
          if (!window.location.href.includes("/signin")) {
            window.location.href = "/signout";
          }
        }
        return Promise.reject(error);
      }
    );
  }

  updateClient = (newToken: string, expiry: Date | null) => {
    this.token = newToken;
    if (expiry) this.tokenExpiry = expiry;

    // Update API
    const headers: AxiosRequestHeaders = {
      Accept: "application/json",
    };

    if (this.token) {
      headers.Authorization = `Bearer ${this.token}`;
    }

    this.client = axios.create({
      baseURL: this.apiBaseUrl,
      timeout: 31000,
      headers: headers,
    });

    this.client.interceptors.request.use((request) => {
      if (!this.tokenExpiry || (this.tokenExpiry && new Date() > this.tokenExpiry)) {
        if (!window.location.href.includes("/signin")) {
          window.location.href = "/signout";
        }
      }
      return request;
    });

    this.client.interceptors.response.use(
      (response) => response,
      (error) => {
        if ([401, 403].includes(error.response?.status)) {
          window.location.href = "/signout";
        }
        return Promise.reject(error);
      }
    );
    return this.client;
  };

  // Required endpoints
  // Airports
  getAirports = (
    page: number,
    size: number,
    searchQuery: string,
    searchField: string,
    sortOrder: string,
    sortField: string
  ) => {
    return this.client.get(`/airports`, {
      params: {
        page,
        size,
        searchQuery,
        searchField,
        sortOrder,
        sortField,
      },
    });
  };

  getAirportById = (id: string) => {
    return this.client.get(`/airports/${id}`);
  };

  createAirport = (airportName: string) => {
    return this.client.post(`/airports`, {
      name: airportName,
    });
  };

  updateAirportById = (id: string, airportName: string) => {
    return this.client.patch(`/airports/${id}`, {
      name: airportName,
    });
  };

  enableAirportById = (id: string) => {
    return this.client.post(`/airports/enable/${id}`);
  };

  disableAirportById = (id: string) => {
    return this.client.post(`/airports/disable/${id}`);
  };

  deleteAirportById = (id: string) => {
    return this.client.delete(`/airports/${id}`);
  };

  checkIfAirportCanBeDeleted = (id: string) => {
    return this.client.get(`/airports/delete/check/${id}`);
  };

  // API Keys
  getApiKeys = (
    page: number,
    size: number,
    searchQuery: string,
    searchField: string,
    sortOrder: string,
    sortField: string
  ) => {
    return this.client.get(`/security/api-keys`, {
      params: {
        page,
        size,
        searchQuery,
        searchField,
        sortOrder,
        sortField,
      },
    });
  };

  getApiKeyById = (id: string) => {
    return this.client.get(`/security/api-keys/${id}`);
  };

  createApiKey = (description: string) => {
    return this.client.post(`/security/api-keys`, {
      description,
    });
  };

  updateApiKeyById = (id: string, description: string) => {
    return this.client.patch(`/security/api-keys/${id}`, {
      description,
    });
  };

  enableApiKeyById = (id: string) => {
    return this.client.post(`/security/api-keys/enable/${id}`);
  };

  disableApiKeyById = (id: string) => {
    return this.client.post(`/security/api-keys/disable/${id}`);
  };

  deleteApiKeyById = (id: string) => {
    return this.client.delete(`/security/api-keys/${id}`);
  };

  // ASIC Management
  getAsics = (
    page: number,
    size: number,
    searchQuery: string,
    searchField: string,
    sortOrder: string,
    sortField: string,
    source?: string
  ) => {
    return this.client.get(`/asics`, {
      params: {
        page,
        size,
        searchQuery,
        searchField,
        sortOrder,
        sortField,
        source,
      },
    });
  };

  getAsicById = (id: string) => {
    return this.client.get(`/asics/${id}`);
  };

  getAsicByAsicId = (asicId: string) => {
    return this.client.get(`/asics/asic-id/${asicId}`);
  };

  verifyManualAsic = (
    id: string,
    asicId: string,
    firstName: string,
    lastName: string,
    company: string,
    airportCode: string,
    expiryDate: string,
    active: boolean
  ) => {
    const requestBody = {
      asicId,
      firstName,
      lastName,
      company,
      airportCode,
      asicExpiryDate: expiryDate,
      active,
    };
    return this.client.patch(`/asics/verify/${id}`, requestBody);
  };

  reviewManualAsic = (
    id: string,
    asicId: string,
    firstName: string,
    lastName: string,
    company: string,
    airportCode: string,
    expiryDate: string,
    active: boolean
  ) => {
    const requestBody = {
      asicId,
      firstName,
      lastName,
      company,
      airportCode,
      asicExpiryDate: expiryDate,
      active,
    };
    return this.client.patch(`/asics/review/${id}`, requestBody);
  };

  deleteManualAsic = (id: string) => {
    return this.client.delete(`/asics/${id}`);
  };

  // ASIC Import
  getASICImports = (
    page: number,
    size: number,
    searchQuery: string,
    searchField: string,
    sortOrder: string,
    sortField: string
  ) => {
    return this.client.get(`/asic-import`, {
      params: {
        page,
        size,
        searchQuery,
        searchField,
        sortOrder,
        sortField,
      },
    });
  };

  getASICImportById = (id: string) => {
    return this.client.get(`/asic-import/${id}`);
  };

  importAsicFile = (file: File) => {
    const formData = new FormData();
    formData.append("file", file);
    return this.client.post(`/asic-import`, formData, {
      headers: {
        "Content-Type": "multipart/form-data",
      },
    });
  };

  downloadAsicImportFile = (asicImportId: string) => {
    return this.client.get(`/asic-import/download/${asicImportId}`);
  };

  downloadAsicImportReportFile = (asicImportId: string) => {
    return this.client.get(`/asic-import/download/report/${asicImportId}`);
  };

  // Audit Policies
  getAuditPolicies = (
    page: number,
    size: number,
    searchQuery: string,
    searchField: string,
    sortOrder: string,
    sortField: string
  ) => {
    return this.client.get(`/audit-policies/`, {
      params: {
        page,
        size,
        searchQuery,
        searchField,
        sortOrder,
        sortField,
      },
    });
  };

  getAuditPolicyById = (id: string) => {
    return this.client.get(`/audit-policies/${id}`);
  };

  createAuditPolicy = (newAuditPolicy: CreateAuditPolicyRequest) => {
    return this.client.post(`/audit-policies`, newAuditPolicy);
  };

  updateAuditPolicyById = (id: string, updatedAuditPolicy: UpdateAuditPolicyRequest) => {
    return this.client.patch(`/audit-policies/${id}`, updatedAuditPolicy);
  };

  checkIfAuditPolicyCanBeDeleted = (id: string) => {
    return this.client.get(`/audit-policies/delete/check/${id}`);
  };

  deleteAuditPolicyById = (id: string) => {
    return this.client.delete(`/audit-policies/${id}`);
  };

  // Locations
  getLocations = (
    page: number,
    size: number,
    searchQuery: string,
    searchField: string,
    sortOrder: string,
    sortField: string
  ) => {
    return this.client.get(`/admin/locations/`, {
      params: {
        page,
        size,
        searchQuery,
        searchField,
        sortOrder,
        sortField,
      },
    });
  };

  getLocationById = (id: string) => {
    return this.client.get(`/locations/${id}`);
  };

  createLocation = (newLocation: CreateLocationRequest) => {
    return this.client.post(`/locations`, newLocation);
  };

  updateLocationById = (id: string, updatedLocation: UpdateLocationRequest) => {
    return this.client.patch(`/locations/${id}`, updatedLocation);
  };

  deleteLocationById = (id: string) => {
    return this.client.delete(`/locations/${id}`);
  };

  createLocationLane = (locationId: string, laneName: string) => {
    return this.client.post(`/locations/${locationId}/lanes`, {
      name: laneName,
    });
  };

  updateLocationLaneById = (locationId: string, laneId: string, laneName: string) => {
    return this.client.put(`/locations/${locationId}/lanes/${laneId}`, {
      name: laneName,
    });
  };

  deleteLocationLaneById = (locationId: string, laneId: string) => {
    return this.client.delete(`/locations/${locationId}/lanes/${laneId}`);
  };

  // Location Access Levels (unified kiosk app)
  getLocationAccessLevels = (locationId: string) => {
    return this.client.get(`/locations/${locationId}/access-levels`);
  };

  createLocationAccessLevel = (
    locationId: string,
    accessLevel: SaveLocationAccessLevelRequest
  ) => {
    return this.client.post(`/locations/${locationId}/access-levels`, accessLevel);
  };

  updateLocationAccessLevelById = (
    locationId: string,
    accessLevelId: string,
    updates: Partial<SaveLocationAccessLevelRequest>
  ) => {
    return this.client.patch(`/locations/${locationId}/access-levels/${accessLevelId}`, updates);
  };

  deleteLocationAccessLevelById = (locationId: string, accessLevelId: string) => {
    return this.client.delete(`/locations/${locationId}/access-levels/${accessLevelId}`);
  };

  // Location Operational Needs (per AEV location, shown on its PreCheck kiosks)
  getLocationOperationalNeeds = (locationId: string) => {
    return this.client.get(`/locations/${locationId}/operational-needs`);
  };

  createLocationOperationalNeed = (
    locationId: string,
    operationalNeed: SaveLocationOperationalNeedRequest
  ) => {
    return this.client.post(`/locations/${locationId}/operational-needs`, operationalNeed);
  };

  updateLocationOperationalNeedById = (
    locationId: string,
    operationalNeedId: string,
    updates: Partial<SaveLocationOperationalNeedRequest>
  ) => {
    return this.client.patch(
      `/locations/${locationId}/operational-needs/${operationalNeedId}`,
      updates
    );
  };

  deleteLocationOperationalNeedById = (locationId: string, operationalNeedId: string) => {
    return this.client.delete(`/locations/${locationId}/operational-needs/${operationalNeedId}`);
  };

  // Location Audit Configurations
  getLocationAuditConfigurationById = (id: string) => {
    return this.client.get(`/location-audit-configurations/${id}`);
  };

  getRandomLocationAuditConfigurationAuditPolicyByLocationId = (locationId: string) => {
    return this.client.get(`/location-audit-configurations/${locationId}/random`);
  };

  createLocationAuditConfiguration = (
    newLocationAuditConfiguration: CreateLocationAuditConfigurationRequest
  ) => {
    return this.client.post(`/location-audit-configurations`, newLocationAuditConfiguration);
  };

  deleteLocationAuditConfigurationById = (id: string) => {
    return this.client.delete(`/location-audit-configurations/${id}`);
  };

  // Notes
  getNotes = (
    page: number,
    size: number,
    searchQuery: string,
    searchField: string,
    sortOrder: string,
    sortField: string
  ) => {
    return this.client.get(`/notes`, {
      params: {
        page,
        size,
        searchQuery,
        searchField,
        sortOrder,
        sortField,
      },
    });
  };

  createNote = (label: string) => {
    return this.client.post(`/notes`, {
      label,
    });
  };

  getNoteById = (id: string) => {
    return this.client.get(`/notes/${id}`);
  };

  updateNoteById = (id: string, label: string) => {
    return this.client.patch(`/notes/${id}`, { label });
  };

  deleteNoteById = (id: string) => {
    return this.client.delete(`/notes/${id}`);
  };

  // Operational Neeeds
  getOperationalNeeds = (
    page: number,
    size: number,
    searchQuery: string,
    searchField: string,
    sortOrder: string,
    sortField: string
  ) => {
    return this.client.get(`/operational-needs`, {
      params: {
        page,
        size,
        searchQuery,
        searchField,
        sortOrder,
        sortField,
      },
    });
  };

  createOperationalNeeds = (name: string) => {
    return this.client.post(`/operational-needs`, {
      name,
    });
  };

  getOperationalNeedById = (id: string) => {
    return this.client.get(`/operational-needs/${id}`);
  };

  updateOperationalNeedById = (id: string, name: string) => {
    return this.client.patch(`/operational-needs/${id}`, { name });
  };

  deleteOperationalNeedById = (id: string) => {
    return this.client.delete(`/operational-needs/${id}`);
  };

  // Randomized Checks Groups
  getRandomizedChecksGroups = (
    page: number,
    size: number,
    searchQuery: string,
    searchField: string,
    sortOrder: string,
    sortField: string
  ) => {
    return this.client.get(`/randomized-checks-groups`, {
      params: {
        page,
        size,
        searchQuery,
        searchField,
        sortOrder,
        sortField,
      },
    });
  };

  createRandomizedChecksGroup = (name: string) => {
    return this.client.post(`/randomized-checks-groups`, {
      name,
    });
  };

  getRandomizedChecksGroupById = (id: string) => {
    return this.client.get(`/randomized-checks-groups/${id}`);
  };

  updateRandomizedChecksGroupById = (id: string, data: any) => {
    return this.client.patch(`/randomized-checks-groups/${id}`, data);
  };

  deleteRandomizedChecksGroupById = (id: string) => {
    return this.client.delete(`/randomized-checks-groups/${id}`);
  };

  // Reports
  getReports = (
    page: number,
    size: number,
    searchQuery: string,
    searchField: string,
    sortOrder: string,
    sortField: string,
    type?: string
  ) => {
    return this.client.get(`/reports`, {
      params: {
        page,
        size,
        searchQuery,
        searchField,
        sortOrder,
        sortField,
        type,
      },
    });
  };

  getReportById = (id: string) => {
    return this.client.get(`/reports/${id}`);
  };

  downloadReportFile = (reportId: string) => {
    return this.client.get(`/reports/download/${reportId}`);
  };

  deleteReportById = (id: string) => {
    return this.client.delete(`/reports/${id}`);
  };

  // Report Configurations
  getReportConfigurationByReportType = (reportType: string) => {
    return this.client.get(`/reports/configuration/${reportType}`);
  };

  createReportConfigurationForReportType = (
    reportType: string,
    body: CreateReportConfigurationRequest
  ) => {
    return this.client.post(`/reports/configuration/${reportType}`, body);
  };

  updateReportConfigurationForReportType = (
    reportType: string,
    body: UpdateReportConfigurationRequest
  ) => {
    return this.client.patch(`/reports/configuration/${reportType}`, body);
  };

  deleteReportConfigurationForReportType = (reportType: string) => {
    return this.client.delete(`/reports/configuration/${reportType}`);
  };

  // Security Checks
  getSecurityChecks = (
    page: number,
    size: number,
    searchQuery: string,
    searchField: string,
    sortOrder: string,
    sortField: string,
    type?: string
  ) => {
    return this.client.get(`/security-checks`, {
      params: {
        page,
        size,
        searchQuery,
        searchField,
        sortOrder,
        sortField,
        type,
      },
    });
  };

  getRandomVehicleSecurityChecks = () => {
    return this.client.get(`/security-checks/random/vehicle`);
  };

  getSecurityCheckById = (securityCheckId: string) => {
    return this.client.get(`/security-checks/${securityCheckId}`);
  };

  createSecurityCheck = (data: CreateSecurityCheckRequest) => {
    return this.client.post(`/security-checks`, data);
  };

  updateSecurityCheckById = (id: string, data: UpdateSecurityCheckRequest) => {
    return this.client.patch(`/security-checks/${id}`, data);
  };

  enableSecurityCheckById = (id: string) => {
    return this.client.post(`/security-checks/enable/${id}`);
  };

  disableSecurityCheckById = (id: string) => {
    return this.client.post(`/security-checks/disable/${id}`);
  };

  // Security Tiers
  getSecurityTiers = (
    page: number,
    size: number,
    searchQuery: string,
    searchField: string,
    sortOrder: string,
    sortField: string
  ) => {
    return this.client.get(`/security-tiers`, {
      params: {
        page,
        size,
        searchQuery,
        searchField,
        sortOrder,
        sortField,
      },
    });
  };

  getSecurityTierById = (securityTierId: string) => {
    return this.client.get(`/security-tiers/${securityTierId}`);
  };

  createSecurityTier = (data: CreateSecurityTierRequest) => {
    return this.client.post(`/security-tiers`, data);
  };

  updateSecurityTierById = (id: string, data: UpdateSecurityTierRequest) => {
    return this.client.patch(`/security-tiers/${id}`, data);
  };

  deleteSecurityTierById = (id: string) => {
    return this.client.delete(`/security-tiers/${id}`);
  };

  // Self Serve Audits
  getSelfServeAudits = (
    page: number,
    size: number,
    searchQuery: string,
    searchField: string,
    sortOrder: string,
    sortField: string,
    fromDate?: string,
    toDate?: string
  ) => {
    return this.client.get(`/self-serve-submissions`, {
      params: {
        page,
        size,
        searchQuery,
        searchField,
        sortOrder,
        sortField,
        fromDate,
        toDate,
      },
    });
  };

  getSelfServeAuditById = (selfServeAuditId: string) => {
    return this.client.get(`/self-serve-submissions/${selfServeAuditId}`);
  };

  getSelfServeAuditFileById = (selfServeAuditId: string, filePath: string) => {
    return this.client.get(`/self-serve-submissions/storage/${selfServeAuditId}`, {
      params: {
        filePath,
      },
    });
  };

  getSelfServeSubmissionsCount = (
    searchQuery: string,
    searchField: string,
    fromDate?: string,
    toDate?: string
  ) => {
    return this.client.get(`/self-serve-submissions/count`, {
      params: {
        searchQuery,
        searchField,
        fromDate,
        toDate,
      },
    });
  };

  exportSelfServeSubmissions = async (
    searchQuery: string,
    searchField: string,
    fromDate?: string,
    toDate?: string
  ) => {
    return this.client.get("/self-serve-submissions/export/csv", {
      responseType: "blob",
      timeout: 0,
      params: {
        searchQuery,
        searchField,
        fromDate,
        toDate,
      },
    });
  };

  exportSelfServeSubmissionsToCsv = (selfServeSubmissionIds: string[]) => {
    return this.client.post(`/self-serve-submissions/export/csv`, {
      selfServeSubmissionIds,
    });
  };

  // Self Serve Locations

  getSelfServeLocations = (
    page: number,
    size: number,
    searchQuery: string,
    searchField: string,
    sortOrder: string,
    sortField: string
  ) => {
    return this.client.get(`/self-serve-locations`, {
      params: {
        page,
        size,
        searchQuery,
        searchField,
        sortOrder,
        sortField,
      },
    });
  };

  getUserSelfServeLocations = (
    page: number,
    size: number,
    searchQuery: string,
    searchField: string,
    sortOrder: string,
    sortField: string
  ) => {
    return this.client.get(`/user-self-serve-locations`, {
      params: {
        page,
        size,
        searchQuery,
        searchField,
        sortOrder,
        sortField,
      },
    });
  };

  createSelfServeLocation = (name: string) => {
    return this.client.post(`/self-serve-locations`, {
      name,
    });
  };

  getSelfServeLocationById = (id: string) => {
    return this.client.get(`/self-serve-locations/${id}`);
  };

  updateSelfServeLocationById = (id: string, data: any) => {
    return this.client.patch(`/self-serve-locations/${id}`, data);
  };

  deleteSelfServeLocationById = (id: string) => {
    return this.client.delete(`/self-serve-locations/${id}`);
  };

  // Tablets (devices)

  getDevices = (
    page: number,
    size: number,
    searchQuery: string,
    searchField: string,
    sortOrder: string,
    sortField: string
  ) => {
    return this.client.get(`/devices`, {
      params: {
        page,
        size,
        searchQuery,
        searchField,
        sortOrder,
        sortField,
      },
    });
  };

  getDeviceById = (id: string) => {
    return this.client.get(`/devices/${id}`);
  };

  createDevice = (data: {
    deviceIdentifier: string;
    appType: string;
    locationId?: string;
    selfServeLocationId?: string;
    brand?: string;
    model?: string;
    operatingSystem?: string;
  }) => {
    return this.client.post(`/devices`, data);
  };

  updateDeviceById = (id: string, data: any) => {
    return this.client.patch(`/devices/${id}`, data);
  };

  deleteDeviceById = (id: string) => {
    return this.client.delete(`/devices/${id}`);
  };

  createSelfServeLocationOperationalRequirement = (
    locationId: string,
    operationalRequirementName: string
  ) => {
    return this.client.post(`/self-serve-locations/${locationId}/operational-requirements`, {
      name: operationalRequirementName,
    });
  };

  updateSelfServeLocationOperationalRequirementById = (
    locationId: string,
    operationalRequirementId: string,
    operationalRequirementName: string
  ) => {
    return this.client.put(
      `/self-serve-locations/${locationId}/operational-requirements/${operationalRequirementId}`,
      {
        name: operationalRequirementName,
      }
    );
  };

  deleteSelfServeLocationOperationalRequirementById = (
    locationId: string,
    operationalRequirementId: string
  ) => {
    return this.client.delete(
      `/self-serve-locations/${locationId}/operational-requirements/${operationalRequirementId}`
    );
  };

  // Person Security Submissions
  getPersonSecuritySubmissions = (
    page: number,
    size: number,
    searchQuery: string,
    searchField: string,
    sortOrder: string,
    sortField: string,
    fromDate?: string,
    toDate?: string,
    timezone?: string
  ) => {
    return this.client.get(`/person-security-submissions`, {
      params: {
        page,
        size,
        searchQuery,
        searchField,
        sortOrder,
        sortField,
        fromDate,
        toDate,
        timezone,
      },
    });
  };

  getPersonSecuritySubmissionsCount = (
    searchQuery: string,
    searchField: string,
    fromDate?: string,
    toDate?: string
  ) => {
    return this.client.get(`/person-security-submissions/count`, {
      params: {
        searchQuery,
        searchField,
        fromDate,
        toDate,
      },
    });
  };

  /**
   * Aggregates for the admin dashboard. Dates are inclusive YYYY-MM-DD in
   * the airport's local time; the backend does all the counting.
   */
  getPersonSecuritySubmissionsDashboard = (
    fromDate: string,
    toDate: string,
    timezone: string,
    dataset?: DashboardDataset,
    location?: string
  ) => {
    return this.client.get<DashboardStats>(`/person-security-submissions/dashboard`, {
      params: { fromDate, toDate, timezone, dataset, location },
    });
  };

  /**
   * Expected submissions per day across an inclusive window (up to 90 days),
   * built server-side from the same weeks last year and recent volume. A
   * window that has already started is forecast as at its first day, so it
   * can be compared fairly with the actuals.
   */
  getPersonSecuritySubmissionsForecast = (
    fromDate: string,
    toDate: string,
    timezone: string,
    dataset?: DashboardDataset,
    location?: string
  ) => {
    return this.client.get<DashboardForecast>(
      `/person-security-submissions/dashboard/forecast`,
      { params: { fromDate, toDate, timezone, dataset, location } }
    );
  };

  getPersonSecuritySubmissionsForecastSummary = (timezone: string, dataset?: DashboardDataset) => {
    return this.client.get<DashboardForecastSummary>(
      `/person-security-submissions/dashboard/forecast/summary`,
      { params: { timezone, dataset } }
    );
  };

  exportPersonSecuritySubmissions = async (
    searchQuery: string,
    searchField: string,
    fromDate?: string,
    toDate?: string
  ) => {
    return this.client.get("/person-security-submissions/export/csv", {
      responseType: "blob",
      timeout: 0,
      params: {
        searchQuery,
        searchField,
        fromDate,
        toDate,
      },
    });
  };

  getPersonSecuritySubmissionById = (personSecuritySubmissionId: string) => {
    return this.client.get(`/person-security-submissions/${personSecuritySubmissionId}`);
  };

  getPersonSecuritySubmissionFileById = (personSecuritySubmissionId: string, filePath: string) => {
    return this.client.get(`/person-security-submissions/storage/${personSecuritySubmissionId}`, {
      params: {
        filePath,
      },
    });
  };

  exportPersonSecuritySubmissionsToCsv = (personSecuritySubmissionIds: string[]) => {
    return this.client.post(`/person-security-submissions/export/csv`, {
      personSecuritySubmissionIds,
    });
  };

  // Person Security Submission Checkins
  getPersonSecuritySubmissionCheckins = (
    page: number,
    size: number,
    searchQuery: string,
    searchField: string,
    sortOrder: string,
    sortField: string,
    fromDate?: string,
    toDate?: string
  ) => {
    return this.client.get(`/person-security-submission-checkins`, {
      params: {
        page,
        size,
        searchQuery,
        searchField,
        sortOrder,
        sortField,
        fromDate,
        toDate,
      },
    });
  };

  getPersonSecuritySubmissionCheckinById = (personSecuritySubmissionCheckinId: string) => {
    return this.client.get(
      `/person-security-submission-checkins/${personSecuritySubmissionCheckinId}`
    );
  };

  approvePersonSecuritySubmissionCheckinById = (personSecuritySubmissionCheckinId: string) => {
    return this.client.post(
      `/person-security-submission-checkins/approve/${personSecuritySubmissionCheckinId}`
    );
  };

  declinePersonSecuritySubmissionCheckinById = (personSecuritySubmissionCheckinId: string) => {
    return this.client.post(
      `/person-security-submission-checkins/decline/${personSecuritySubmissionCheckinId}`
    );
  };

  deletePersonSecuritySubmissionCheckinById = (personSecuritySubmissionCheckinId: string) => {
    return this.client.delete(
      `/person-security-submission-checkins/${personSecuritySubmissionCheckinId}`
    );
  };

  getPersonSecuritySubmissionCheckinFileById = (
    personSecuritySubmissionCheckinId: string,
    filePath: string
  ) => {
    return this.client.get(
      `/person-security-submission-checkins/storage/${personSecuritySubmissionCheckinId}`,
      {
        params: {
          filePath,
        },
      }
    );
  };

  getVehicleCheckins = (
    page: number,
    size: number,
    searchQuery: string,
    searchField: string,
    sortOrder: string,
    sortField: string,
    fromDate?: string,
    toDate?: string,
    timezone?: string
  ) => {
    return this.client.get(`/vehicle-checkins/`, {
      params: {
        page,
        size,
        searchQuery,
        searchField,
        sortOrder,
        sortField,
        fromDate,
        toDate,
        timezone,
      },
    });
  };

  deleteVehicleCheckinById = (id: string) => {
    return this.client.delete(`/vehicle-checkins/${id}`);
  };

  // Users
  getUsers = (
    page: number,
    size: number,
    searchQuery: string,
    searchField: string,
    sortOrder: string,
    sortField: string,
    role: string
  ) => {
    return this.client.get(`/users`, {
      params: {
        page,
        size,
        searchQuery,
        searchField,
        sortOrder,
        sortField,
        role,
      },
    });
  };

  createUser = (newUser: CreateUserRequest) => {
    return this.client.post(`/users`, newUser);
  };

  loginUser = (username: string, password: string) => {
    // Need to use an unintercepted axios instance to login so that the interceptor
    // doesn't navigate to signout if credentials invalid
    const uninterceptedAxiosInstance = axios.create({
      baseURL: this.apiBaseUrl,
      timeout: 31000,
    });
    return uninterceptedAxiosInstance.post(`/users/login`, {
      username,
      password,
    });
  };

  getUserById = (id: string) => {
    return this.client.get(`/users/${id}`);
  };

  updateUserById = (id: string, updatedUser: UpdateUserRequest) => {
    return this.client.patch(`/users/${id}`, updatedUser);
  };

  deleteUserById = (id: string) => {
    return this.client.delete(`users/${id}`);
  };

  // Offline Access (outage resilience): the shared PIN officers use to record
  // entries on the Officer Dashboard while the backend is unreachable.
  getOfflinePinStatus = () => {
    return this.client.get(`/security/offline-pin`);
  };

  // Returns the plaintext once, and records who asked. Every reveal is
  // audited because the PIN authorises writing into the security database.
  revealOfflinePin = () => {
    return this.client.post(`/security/offline-pin/reveal`);
  };

  rotateOfflinePin = (pin?: string) => {
    return this.client.post(`/security/offline-pin/rotate`, pin ? { pin } : {});
  };

  updateOfflinePinConfig = (config: UpdateOfflineAccessConfigRequest) => {
    return this.client.patch(`/security/offline-pin`, config);
  };

  getOfflinePinRevealLog = (size: number = 20) => {
    return this.client.get(`/security/offline-pin/reveal-log`, {
      params: { size },
    });
  };

  // ASIC Exemptions - Operations Staff
  getAsicExemptions = (
    page: number,
    size: number,
    searchQuery: string,
    searchField: string,
    sortOrder: string,
    sortField: string,
    fromDate?: string,
    toDate?: string,
    timezone?: string
  ) => {
    return this.client.get(`/asic-exemptions`, {
      params: {
        page,
        size,
        searchQuery,
        searchField,
        sortOrder,
        sortField,
        fromDate,
        toDate,
        timezone,
      },
    });
  };

  deleteAsicExemptionById = (id: string) => {
    return this.client.delete(`/asic-exemptions/${id}`);
  };

  addExemption = (asic: string, firstName: string, lastName: string) => {
    return this.client.post(`/asic-exemptions`, {
      asicId: asic,
      firstName,
      lastName,
    });
  };

  // ASIC Flags
  getAsicFlagById = (id: string) => {
    return this.client.get(`/asics/${id}/flag`);
  };

  setAsicFlag = (id: string, flagStatus: boolean, flagNote?: string | null) => {
    return this.client.post(`/asics/${id}/flag`, {
      flagStatus,
      flagNote,
    });
  };

  clearAsicFlag = (id: string) => {
    return this.client.delete(`/asics/${id}/flag`);
  };
}

export const apiV3 = new ApiV3();
